import { supabase } from '../lib/supabase';
import openaiService from './openaiService';
import getTranscribeService from './getTranscribeServiceSecure';
import { generateThumbnail } from '../utils/thumbnailUtils';
import topicService from './topicService';
import cache from '../utils/cache';
import vaultService from './vaultService';
import vaultWriterService from './vaultWriterService';

class ContentService {
  // Map content type to DeepSearch source type
  // Returns PLATFORM NAME for social media (instagram, youtube, tiktok) so badges show platform
  // Content type (video, image) is stored in meta.content_type for filtering
  mapContentTypeToDeepSearchType(contentType, channel) {
    if (contentType === 'text' || !contentType) return 'note';
    
    // Social media platforms - return platform name for badge display
    // The actual content type (video/image) is preserved in meta.content_type
    if (channel === 'Instagram') return 'instagram';
    if (channel === 'YouTube') return 'youtube';
    if (channel === 'TikTok') return 'tiktok';
    if (channel === 'Twitter') return 'twitter';
    if (channel === 'Reddit') return 'reddit';
    if (channel === 'LinkedIn') return 'linkedin';
    
    // Non-platform content types
    if (contentType === 'pdf') return 'pdf';
    if (contentType === 'audio') return 'audio';
    if (contentType === 'transcript') return 'transcript';
    if (contentType === 'image') return 'image';
    if (contentType === 'video' || contentType === 'reel') return 'video';
    
    return 'link';
  }

  // Channel detection patterns
  static channelPatterns = {
    'Instagram': /instagram\.com/,
    'YouTube': /youtube\.com|youtu\.be/,
    'TikTok': /tiktok\.com/,
    'Twitter': /twitter\.com|x\.com/,
    'Facebook': /facebook\.com/,
    'Pinterest': /pinterest\.com/,
    'Threads': /threads\.net/,
    'Podcasts': /spotify\.com\/episode|apple\.com\/podcast/,
    'Web': /^https?:\/\//
  };

  // Detect channel from URL
  static detectChannel(url) {
    if (!url) return 'Web';
    
    for (const [channel, pattern] of Object.entries(this.channelPatterns)) {
      if (pattern.test(url)) {
        return channel;
      }
    }
    return 'Web';
  }

  // Check if content with URL already exists for a specific user
  async getContentByUrl(url, userId = null) {
    try {
      if (!url) return { data: null, error: null };
      
      let query = supabase.schema('public').from('instagram_content').select('*').eq('url', url);
      
      // If userId is provided, filter by user_id to check for user-specific duplicates
      if (userId) {
        query = query?.eq('user_id', userId);
      }
      
      // Use maybeSingle() instead of single() to avoid 406 error when no row exists
      const { data, error } = await query?.maybeSingle();

      if (error) {
        console.warn('⚠️ Error checking for duplicate content:', error);
        return { data: null, error: null }; // Treat errors as "not found"
      }
      
      return { data: data || null, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Create new content with channel detection and AI analysis
  async addContent(contentData) {
    try {
      console.log('🔍 ContentService.addContent called with:', contentData);
      
      // Ensure "All" collection exists (items are automatically in "All")
      if (contentData?.user_id) {
        const topicService = await import('./topicService').then(m => m.default);
        await topicService.ensureAllCollection(contentData.user_id);
      }
      
      // Check if URL already exists if URL is provided
      if (contentData?.url) {
        console.log('🔍 Checking for existing content with URL:', contentData.url);
        const { data: existingContent } = await this.getContentByUrl(contentData?.url, contentData?.user_id);
        if (existingContent) {
          console.log('⚠️ Content already exists, updating to new collection:', existingContent);
          
          // Update the existing content to the new collection
          const { data: updatedContent, error: updateError } = await supabase
            .schema('public')
            .from('instagram_content')
            .update({ topic_id: contentData.topic_id })
            .eq('id', existingContent.id)
            .select()
            .single();
            
          if (updateError) {
            console.error('❌ Failed to update existing content:', updateError);
            return { 
              data: existingContent, 
              error: null, 
              duplicate: true,
              message: 'Content exists but could not be moved to new collection'
            };
          }
          
          console.log('✅ Content moved to new collection:', updatedContent);
          return { 
            data: updatedContent, 
            error: null, 
            duplicate: false,
            message: 'Content moved to new collection'
          };
        }
      }

      // Detect channel if not provided
      if (contentData?.url && !contentData?.channel) {
        contentData.channel = ContentService.detectChannel(contentData.url);
        console.log('🎯 Detected channel:', contentData.channel);
      } else if (!contentData?.url) {
        // For text content, set a default channel
        contentData.channel = 'Text';
        console.log('📝 Text content - setting channel to Text');
      }

      // Determine content type based on channel and URL
      if (!contentData?.content_type) {
        contentData.content_type = this.determineContentType(contentData?.url, contentData?.channel);
      }

      // Get platform icon but don't save it to database (it doesn't exist in schema)
      const platformIcon = this.getPlatformIcon(contentData?.channel);

      // Remove fields that don't exist in database schema
      const insertData = { ...contentData };
      delete insertData.platform_icon; // This column doesn't exist in the database
      
      // Ensure channel is always set
      if (!insertData.channel) {
        insertData.channel = 'Web'; // Default fallback
        console.log('⚠️ No channel detected, using default: Web');
      }
      
      // NEW: Use unified vault writer - writes ONLY to deepsearch.items
      console.log('💾 Saving content via VaultWriter:', insertData.title);
      try {
        // Save through vault writer - goes to deepsearch.items with full enrichment
        const saved = await vaultWriterService.saveItem(insertData, {
          sourceTable: 'public.instagram_content', // For traceability
          skipEnrichment: false, // Enable AI enrichment
          skipEmbedding: false,  // Enable embeddings
        });
        
        console.log('✅ Content saved to deepsearch.items with enrichment:', saved.id);
        
        // Return in expected format
        return { 
          data: {
            id: saved.id,
            user_id: saved.userId,
            title: saved.title,
            description: saved.description,
            url: saved.url,
            thumbnail_url: saved.thumbnailUrl,
            content_type: saved.sourceType,
            channel: saved.channel,
            created_at: saved.createdAt,
            updated_at: saved.updatedAt,
          }, 
          error: null, 
          duplicate: false 
        };
      } catch (error) {
        console.error('❌ VaultWriter save failed:', error);
        console.error('❌ Error details:', {
          message: error.message,
          code: error.code,
          details: error.details
        });
        throw error;
      }
    } catch (error) {
      // Handle duplicate key error specifically
      if (error?.message?.includes('duplicate key value violates unique constraint "instagram_content_url_key"')) {
        // Try to fetch the existing content
        const { data: existingContent } = await this.getContentByUrl(contentData?.url, contentData?.user_id);
        return { 
          data: existingContent, 
          error: null, 
          duplicate: true,
          message: 'Content with this URL already exists'
        };
      }
      return { data: null, error };
    }
  }

  // Determine content type based on URL and channel
  determineContentType(url, channel) {
    if (!url) return 'post';
    
    const urlLower = url.toLowerCase();
    
    if (urlLower.includes('reel') || urlLower.includes('video')) return 'reel';
    if (urlLower.includes('story')) return 'story';
    if (urlLower.includes('episode') || urlLower.includes('podcast')) return 'podcast';
    if (urlLower.includes('article') || urlLower.includes('blog')) return 'article';
    if (urlLower.includes('email')) return 'email';
    if (urlLower.includes('message') || urlLower.includes('chat')) return 'message';
    
    return 'post';
  }

  // Get platform icon for channel
  getPlatformIcon(channel) {
    const icons = {
      'Instagram': '📷',
      'YouTube': '▶️',
      'TikTok': '🎵',
      'Twitter': '🐦',
      'Facebook': '👥',
      'Pinterest': '📌',
      'Threads': '🧵',
      'Podcasts': '🎧',
      'Email': '📧',
      'Slack': '💬',
      'Web': '🌐',
      'Recordings': '🎙️'
    };
    return icons[channel] || '📄';
  }

  // Process content with AI analysis and transcription
  async processContent(contentData) {
    try {
      const { data: content, error: addError } = await this.addContent(contentData);
      if (addError) throw addError;

      // If it's a duplicate, return it
      if (addError?.duplicate) {
        return { data: content, error: null, duplicate: true };
      }

      // Update status to processing
      await this.updateContent(content.id, { analysis_status: 'processing' });

      let transcription = null;
      let aiAnalysis = null;

      // Extract thumbnail for video content
      if (contentData?.url && contentData?.channel) {
        try {
          const thumbnailResult = await this.extractThumbnail(contentData.url, contentData.channel);
          if (thumbnailResult?.data?.thumbnail_url) {
            await this.updateContent(content.id, { 
              thumbnail_url: thumbnailResult.data.thumbnail_url 
            });
          }
        } catch (error) {
          console.warn('Thumbnail extraction failed:', error);
        }
      }

      // Get transcription if it's video/audio content
      if (contentData?.url && this.needsTranscription(contentData?.content_type, contentData?.channel)) {
        try {
          const transcriptionResult = await getTranscribeService.transcribeVideo(contentData.url);
          if (transcriptionResult?.data?.text) {
            const transcriptText = transcriptionResult.data.text;
            transcription = {
              full_transcript: transcriptText,
              text: transcriptText,
              transcript: transcriptText,
              timestamp: new Date().toISOString(),
              source: 'gettranscribe_ai'
            };
            console.log('💾 Saving transcription data:', transcriptText.substring(0, 100));
            await this.updateContent(content.id, { transcription });
            console.log('✅ Transcription saved to database');
          }
        } catch (error) {
          console.warn('Transcription failed:', error);
        }
      }

      // Get AI analysis
      try {
        const analysisResult = await openaiService.analyzeContentForChannelAndTags(
          contentData?.url || contentData?.description || contentData?.title,
          transcription
        );

        if (analysisResult?.data) {
          aiAnalysis = {
            summary: analysisResult.data.summary,
            key_points: analysisResult.data.key_points || [],
            tags: analysisResult.data.tags || [],
            suggested_channel: analysisResult.data.channel,
            suggested_collections: analysisResult.data.suggestedCollections || [],
            confidence_score: analysisResult.data.confidence_score || 0.5,
            main_topics: analysisResult.data.main_topics || [],
            content_metadata: analysisResult.data.content_metadata || {}
          };

          await this.updateContent(content.id, {
            ai_analysis: aiAnalysis,
            key_points: aiAnalysis.key_points,
            tags: aiAnalysis.tags,
            confidence_score: aiAnalysis.confidence_score,
            main_topics: aiAnalysis.main_topics,
            content_metadata: aiAnalysis.content_metadata,
            analysis_status: 'completed'
          });
        }
      } catch (error) {
        console.warn('AI analysis failed:', error);
        await this.updateContent(content.id, { analysis_status: 'failed' });
      }

      return { data: content, error: null, duplicate: false };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Check if content needs transcription
  needsTranscription(contentType, channel) {
    const videoTypes = ['reel', 'video', 'podcast', 'story', 'live'];
    const videoChannels = ['YouTube', 'TikTok', 'Instagram', 'Podcasts', 'Facebook'];
    
    return videoTypes.includes(contentType) || videoChannels.includes(channel);
  }

  // Generate better title from AI analysis
  generateTitleFromAnalysis(aiAnalysis, currentTitle) {
    if (!aiAnalysis) return currentTitle;
    
    // If we have main topics, use them to create a better title
    if (aiAnalysis.main_topics && aiAnalysis.main_topics.length > 0) {
      const topics = aiAnalysis.main_topics.slice(0, 2).join(' & ');
      return `${topics} - ${aiAnalysis.suggested_channel || 'Content'}`;
    }
    
    // If we have key points, use the first one as title
    if (aiAnalysis.key_points && aiAnalysis.key_points.length > 0) {
      const firstPoint = aiAnalysis.key_points[0];
      if (firstPoint.length <= 60) {
        return firstPoint;
      }
      return firstPoint.substring(0, 57) + '...';
    }
    
    // If we have a summary, extract a title from it
    if (aiAnalysis.summary && typeof aiAnalysis.summary === 'string') {
      const sentences = aiAnalysis.summary.split('.');
      const firstSentence = sentences[0];
      if (firstSentence.length <= 60) {
        return firstSentence;
      }
      return firstSentence.substring(0, 57) + '...';
    }
    
    return currentTitle;
  }

  // Get all content for a user
  async getUserContent(userId) {
    try {
      const { data, error } = await supabase.schema('public').from('instagram_content').select(`
          *,
          topic:topics(*)
        `).eq('user_id', userId).order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Get content by ID
  async getContentById(contentId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('instagram_content')
        .select(`
          *,
          topic:topics(*)
        `)
        .eq('id', contentId)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Get content by channel
  async getContentByChannel(userId, channel) {
    try {
      console.log('🔍 Attempting to load content for channel:', channel);
      
      // Try to query with channel filter first
      const { data, error } = await supabase
        .schema('public')
        .from('instagram_content')
        .select(`
          *,
          topic:topics(*)
        `)
        .eq('user_id', userId)
        .eq('channel', channel)
        .order('created_at', { ascending: false });

      if (error && error.message.includes('channel')) {
        // Channel column doesn't exist, return empty result
        console.warn('⚠️ Channel column not found in instagram_content table');
        console.log('📋 Available columns might not include "channel"');
        return { data: [], error: null };
      }

      if (error) {
        console.error('❌ Database error:', error);
        throw error;
      }
      
      console.log('✅ Successfully loaded content:', data?.length || 0, 'items');
      return { data, error: null };
    } catch (error) {
      console.error('❌ getContentByChannel failed:', error);
      return { data: null, error };
    }
  }

  // Get content by collection (topic)
  // Uses 2-query approach: first get item_ids from topic_items join table, then fetch items
  // NEVER falls back to returning all items - strict guards ensure only collection items are returned
  async getContentByCollection(userId, collectionId) {
    try {
      // TEMPORARY DEBUG LOGS
      console.log('🔍 [getContentByCollection DEBUG] START');
      console.log('🔍 [getContentByCollection DEBUG] topicId passed in:', collectionId);
      console.log('🔍 [getContentByCollection DEBUG] userId used:', userId);

      // Validate inputs
      if (!userId || !collectionId) {
        console.error('[getContentByCollection] Missing userId or collectionId');
        return { data: [], error: new Error('userId and collectionId are required') };
      }

      // Query 1: get item ids from join table
      const { data: links, error: linksError } = await supabase
        .schema('public')
        .from('topic_items')
        .select('item_id, created_at')
        .eq('topic_id', collectionId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      // Guard 1: If Query 1 errors, immediately return empty array with error
      if (linksError) {
        console.error('[getContentByCollection] topic_items error:', linksError);
        return { data: [], error: linksError };
      }

      // Guard 2: Extract item IDs and validate
      const itemIds = (links || []).map(r => r.item_id).filter(Boolean);

      // TEMPORARY DEBUG LOGS
      console.log('🔍 [getContentByCollection DEBUG] itemIds list length from topic_items:', itemIds.length);
      console.log('🔍 [getContentByCollection DEBUG] first 5 itemIds:', itemIds.slice(0, 5));

      // Guard 3: If Query 1 returns 0 rows, immediately return empty array
      if (!itemIds || itemIds.length === 0) {
        console.log(`[getContentByCollection] No items found for collection ${collectionId}`);
        return { data: [], error: null };
      }

      // Guard 4: Hard validation - ensure itemIds is a valid non-empty array
      if (!Array.isArray(itemIds) || itemIds.length === 0) {
        console.error('[getContentByCollection] Invalid itemIds array:', itemIds);
        return { data: [], error: new Error('Invalid itemIds array') };
      }

      // Guard 5: Ensure no undefined/null values in array before calling .in()
      const validItemIds = itemIds.filter(id => id != null && id !== undefined);
      if (validItemIds.length === 0) {
        console.error('[getContentByCollection] No valid item IDs after filtering');
        return { data: [], error: null };
      }

      // Query 2: fetch items (only runs if we have valid itemIds)
      const { data: items, error: itemsError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*')
        .in('id', validItemIds)
        .eq('user_id', userId)
        .not('url', 'like', 'pending://%');

      // TEMPORARY DEBUG LOGS
      console.log('🔍 [getContentByCollection DEBUG] items list length from deepsearch.items:', items?.length || 0);

      if (itemsError) {
        console.error('[getContentByCollection] deepsearch.items error:', itemsError);
        return { data: [], error: itemsError };
      }

      // Preserve join ordering
      const orderIndex = new Map(validItemIds.map((id, idx) => [id, idx]));
      const sorted = (items || []).slice().sort((a, b) => {
        return (orderIndex.get(a.id) ?? 999999) - (orderIndex.get(b.id) ?? 999999);
      });

      console.log(`[getContentByCollection] Returning ${sorted.length} items for collection ${collectionId}`);
      console.log('🔍 [getContentByCollection DEBUG] END');

      return {
        data: sorted.map(item => ({
          ...item,
          topic_id: collectionId,
          _source: 'public.topic_items + deepsearch.items'
        })),
        error: null
      };
    } catch (error) {
      console.error('[getContentByCollection] exception:', error);
      return { data: [], error };
    }
  }

  // Get content by topic (legacy method)
  async getContentByTopic(topicId) {
    try {
      const { data, error } = await supabase.schema('public').from('instagram_content').select('*').eq('topic_id', topicId).order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Update content
  async updateContent(contentId, updates) {
    try {
      console.log('📤 Updating content:', contentId, 'with:', JSON.stringify(updates, null, 2));
      
      // Remove ai_analysis if present (column doesn't exist in schema)
      const sanitizedUpdates = { ...updates };
      if (sanitizedUpdates.ai_analysis) {
        console.warn('⚠️ Removing ai_analysis from updates (column doesn\'t exist)');
        delete sanitizedUpdates.ai_analysis;
      }
      // Remove analysis_error if present (column doesn't exist in schema)
      if (sanitizedUpdates.analysis_error) {
        console.warn('⚠️ Removing analysis_error from updates (column doesn\'t exist). Original update contained:', JSON.stringify(sanitizedUpdates.analysis_error).substring(0, 200));
        delete sanitizedUpdates.analysis_error;
      }
      
      // CRITICAL: Fetch existing item to preserve existing meta fields
      const { data: existingItem, error: fetchError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('meta')
        .eq('id', contentId)
        .single();
      
      if (fetchError) {
        console.warn('⚠️ Could not fetch existing item for meta merge:', fetchError.message);
      }
      
      const existingMeta = existingItem?.meta || {};
      
      // Wrap analysis_data and transcription in meta JSONB for deepsearch.items
      const deepsearchUpdates = { ...sanitizedUpdates };
      
      // Start with existing meta and merge new fields
      const newMeta = { ...existingMeta };
      
      // If analysis_data is being updated, store it in meta
      if (deepsearchUpdates.analysis_data) {
        newMeta.analysis_data = deepsearchUpdates.analysis_data;
        // Also keep ai_analysis for compatibility
        newMeta.ai_analysis = deepsearchUpdates.analysis_data;
        delete deepsearchUpdates.analysis_data; // Remove top-level field
      }
      
      // If transcription is being updated, store it in meta
      if (deepsearchUpdates.transcription) {
        newMeta.transcription = deepsearchUpdates.transcription;
        delete deepsearchUpdates.transcription; // Remove top-level field
      }
      
      // Map status fields
      if (deepsearchUpdates.analysis_status) {
        deepsearchUpdates.status = deepsearchUpdates.analysis_status;
        newMeta.analysis_status = deepsearchUpdates.analysis_status;
        delete deepsearchUpdates.analysis_status;
      }
      
      if (deepsearchUpdates.progress_status) {
        newMeta.progress_status = deepsearchUpdates.progress_status;
        delete deepsearchUpdates.progress_status;
      }
      
      if (deepsearchUpdates.progress_percentage !== undefined) {
        newMeta.progress_percentage = deepsearchUpdates.progress_percentage;
        delete deepsearchUpdates.progress_percentage;
      }
      
      // If updates include meta fields, merge them
      if (deepsearchUpdates.meta) {
        Object.assign(newMeta, deepsearchUpdates.meta);
        delete deepsearchUpdates.meta;
      }
      
      // CRITICAL: Keep thumbnail_url AND also map to image_url for maximum compatibility
      // deepsearch.items has BOTH thumbnail_url and image_url columns
      if (deepsearchUpdates.thumbnail_url) {
        // Keep thumbnail_url in the update (column exists in deepsearch.items)
        // ALSO set image_url for components that check that field
        deepsearchUpdates.image_url = deepsearchUpdates.thumbnail_url;
        // Also store in meta for backward compatibility
        newMeta.thumbnail_url = deepsearchUpdates.thumbnail_url;
        console.log('🖼️ Saving thumbnail_url and image_url:', deepsearchUpdates.thumbnail_url);
      }
      
      // Set the merged meta
      deepsearchUpdates.meta = newMeta;
      
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('items')
        .update(deepsearchUpdates)
        .eq('id', contentId)
        .select()
        .single();

      if (error) {
        console.error('⚠️ Supabase update failed:', error.message);
        throw error;
      }
      
      console.log('✅ Content updated successfully in deepsearch.items');
      return { data, error: null };
    } catch (error) {
      console.error('❌ Update failed:', error);
      return { data: null, error };
    }
  }

  // Get recent content across all channels (for dashboard)
  async getRecentContent(userId, limit = 6, signal = null) {
    const cacheKey = `recent_content_${userId}_${limit}`;
    
    // Check cache first
    const cached = cache.get(cacheKey);
    if (cached) {
      console.log('📦 Returning cached recent content');
      return { data: cached, error: null };
    }

    try {
      console.log('🔄 Fetching recent content from database');
      const { data, error } = await supabase
        .schema('public')
        .from('instagram_content')
        .select(`
          *,
          topic:topics(*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit)
        .abortSignal(signal); // Add abort signal

      if (error) throw error;

      // Cache the result
      cache.set(cacheKey, data);
      
      return { data, error: null };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was cancelled');
        return { data: null, error: null };
      }
      console.error('Get recent content error:', error);
      return { data: null, error };
    }
  }

  // Get content statistics by channel
  async getContentStats(userId) {
    // Return mock data in development mode
    if (import.meta.env.DEV) {
      const mockStats = {
        'YouTube': 5,
        'Twitter': 3,
        'TikTok': 2,
        'Instagram': 4,
        'LinkedIn': 1,
        'Web': 2
      };
      return { data: mockStats, error: null };
    }

    try {
      const { data, error } = await supabase.schema('public').from('instagram_content').select('channel').eq('user_id', userId);

      if (error) throw error;

      const stats = {};
      data?.forEach(item => {
        stats[item.channel] = (stats[item.channel] || 0) + 1;
      });

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Delete content from both instagram_content and deepsearch.items
  async deleteContent(userId, contentId) {
    try {
      const errors = [];
      
      // Try to delete from instagram_content
      const { error: instagramError } = await supabase
        .schema('public')
        .from('instagram_content')
        .delete()
        .eq('user_id', userId)
        .eq('id', contentId);

      if (instagramError && instagramError.code !== 'PGRST116') {
        // PGRST116 = no rows found (not an error if item doesn't exist in this table)
        errors.push({ table: 'instagram_content', error: instagramError });
      }

      // Try to delete from deepsearch.items (for uploaded files)
      const { error: deepsearchError } = await supabase
        .schema('deepsearch')
        .from('items')
        .delete()
        .eq('user_id', userId)
        .eq('id', contentId);

      if (deepsearchError && deepsearchError.code !== 'PGRST116') {
        // PGRST116 = no rows found (not an error if item doesn't exist in this table)
        errors.push({ table: 'deepsearch.items', error: deepsearchError });
      }

      // If we got errors from both tables, that's a real problem
      if (errors.length === 2) {
        throw new Error(`Failed to delete from both tables: ${errors.map(e => e.error.message).join(', ')}`);
      }

      // Success if at least one deletion worked (or both returned PGRST116, meaning item didn't exist)
      console.log('✅ Content deleted successfully', {
        contentId,
        deletedFrom: errors.length === 0 ? 'both tables' : errors[0].table === 'instagram_content' ? 'deepsearch.items only' : 'instagram_content only',
      });
      
      return { data: true, error: null };
    } catch (error) {
      console.error('Delete content error:', error);
      return { data: null, error };
    }
  }


  // Get content by channel
  async getContentByChannel(userId, channel) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('*')
        .eq('user_id', userId)
        .eq('channel', channel)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get content by channel error:', error);
      return { data: null, error };
    }
  }

  // Get content by ID
  async getContentById(contentId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('*')
        .eq('id', contentId)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get content by ID error:', error);
      return { data: null, error };
    }
  }

  // Get all user content
  async getUserContent(userId, limit = 100) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get user content error:', error);
      return { data: null, error };
    }
  }

  // Extract thumbnail from video URL
  async extractThumbnail(url, channel) {
    try {
      console.log('🖼️ Extracting thumbnail for:', url);
      
      // Different thumbnail extraction based on platform
      let thumbnailUrl = null;
      
      if (channel === 'YouTube') {
        // YouTube thumbnail extraction
        const videoId = this.extractYouTubeVideoId(url);
        if (videoId) {
          thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      } else if (channel === 'Instagram') {
        // Instagram thumbnail extraction (limited - would need Instagram API)
        // For now, we'll use a placeholder or try to extract from URL
        thumbnailUrl = this.generateInstagramThumbnail(url);
      } else if (channel === 'TikTok') {
        // TikTok thumbnails will be extracted from GetTranscribe API response
        // during transcription processing. For initial display, use placeholder.
        // The actual thumbnail from GetTranscribe will be saved during transcription.
        thumbnailUrl = this.generateTikTokThumbnail(url);
      } else {
        // Generic thumbnail for other platforms
        thumbnailUrl = this.generateGenericThumbnail(url, channel);
      }

      return {
        data: {
          thumbnail_url: thumbnailUrl,
          extraction_method: 'platform_specific'
        },
        error: null
      };
    } catch (error) {
      console.error('Thumbnail extraction error:', error);
      return {
        data: {
          thumbnail_url: null,
          extraction_method: 'failed'
        },
        error: error.message
      };
    }
  }

  // Extract YouTube video ID from URL
  extractYouTubeVideoId(url) {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }

  // Generate Instagram thumbnail (local SVG)
  generateInstagramThumbnail(url) {
    // Use local SVG placeholder instead of external service
    return '/assets/instagram-placeholder.svg';
  }

  // Generate TikTok thumbnail (local SVG)
  generateTikTokThumbnail(url) {
    // Use local SVG placeholder instead of external service
    return '/assets/tiktok-placeholder.svg';
  }

  // Generate generic thumbnail (local SVG)
  generateGenericThumbnail(url, channel) {
    const placeholders = {
      'Instagram': '/assets/instagram-placeholder.svg',
      'TikTok': '/assets/tiktok-placeholder.svg',
      'YouTube': '/assets/youtube-placeholder.svg',
      'Twitter': '/assets/default-thumbnail.svg',
      'Facebook': '/assets/default-thumbnail.svg',
      'LinkedIn': '/assets/default-thumbnail.svg',
      'Pinterest': '/assets/default-thumbnail.svg',
      'Web': '/assets/default-thumbnail.svg'
    };
    
    return placeholders[channel] || '/assets/default-thumbnail.svg';
  }

  // Legacy methods for backward compatibility
  async createContent(contentData) {
    return this.addContent(contentData);
  }

  // Reprocess existing content with new transcription and AI analysis
  async reprocessContent(contentId) {
    try {
      console.log('🔄 Reprocessing content:', contentId);
      
      // Get the existing content
      const { data: content, error: getError } = await this.getContentById(contentId);
      if (getError || !content) {
        throw new Error('Content not found');
      }

      // Use the processing queue for reprocessing
      const processingQueue = await import('../utils/processingQueue');
      
      const result = await processingQueue.default.addToQueue(content, { 
        forceReTranscribe: true,
        silent: false
      });
      
      if (result.success) {
        console.log('✅ Reprocessing completed successfully');
        return { data: true, error: null };
      } else {
        console.error('❌ Reprocessing failed:', result.error);
        return { data: false, error: result.error };
      }
      
      // Old inline processing logic below - keeping for reference but not using
      /*

      let transcription = null;
      let aiAnalysis = null;

      // Step 1: Transcription (0-30%) with progress tracking
      const needsTranscription = this.needsTranscription(content.content_type, content.channel);
      
      if (content.url && needsTranscription) {
        try {
          console.log('🎬 Starting transcription for:', content.url);
          console.log('📊 Content details:', {
            content_type: content.content_type,
            channel: content.channel,
            url: content.url
          });
          
          // Update progress: Starting transcription
          await this.updateContent(contentId, { 
            progress_percentage: 5,
            progress_status: 'transcribing'
          });
          
          // Use serverless GetTranscribe API
          console.log('🎬 Starting transcription via GetTranscribe API');
          console.log('📡 Calling getTranscribeService.transcribeVideo with URL:', content.url);
          console.log('📡 getTranscribeService:', getTranscribeService);
          
          // Check if getTranscribeService exists
          if (!getTranscribeService) {
            console.error('❌ getTranscribeService is not defined or not imported');
            throw new Error('GetTranscribe service not available');
          }
          
          console.log('📡 About to call getTranscribeService.transcribeVideo...');
          const transcriptionResult = await getTranscribeService.transcribeVideo(content.url);
          console.log('📡 Transcription result received:', transcriptionResult);
          
          if (transcriptionResult?.success && transcriptionResult?.data?.text) {
            // Simple: Extract the text, save it
            transcription = transcriptionResult.data.text;
            console.log('✅ Got transcription text, length:', transcription.length);
            
            await this.updateContent(contentId, { 
              transcription: {
                full_transcript: transcription,
                text: transcription,
                transcript: transcription,
                timestamp: new Date().toISOString(),
                source: 'gettranscribe_ai'
              },
              progress_percentage: 30,
              progress_status: 'transcribing_completed'
            });
            console.log('✅ Transcription saved to database');
            
          } else {
            // Any other result = failed or timed out
            console.error('❌ Transcription not completed:', transcriptionResult);
            await this.updateContent(contentId, { 
              analysis_status: 'failed',
              progress_status: 'failed',
              progress_percentage: 0
            });
            return { data: false, error: { message: transcriptionResult?.error || 'Transcription failed or timed out' } };
          }
        } catch (error) {
          console.error('❌ Transcription error caught in catch block:', error);
          console.error('❌ Error name:', error.name);
          console.error('❌ Error message:', error.message);
          console.error('❌ Error stack:', error.stack);
          await this.updateContent(contentId, { 
            analysis_status: 'failed',
            progress_status: 'failed',
            progress_percentage: 0
          });
          // Don't continue if transcription fails
          return { data: false, error };
        }
      } else {
        console.log('ℹ️ Transcription not needed for this content type:', {
          hasUrl: !!content.url,
          contentType: content.content_type,
          channel: content.channel
        });
        // If no transcription needed, start at 30%
        await this.updateContent(contentId, { 
          progress_percentage: 30,
          progress_status: 'thumbnail_extracting'
        });
      }

      // Step 2: Thumbnail extraction (30-60%)
      if (content.url) {
        try {
          console.log('🖼️ Extracting thumbnail for:', content.url, content.channel);
          
          // Update progress: Starting thumbnail extraction
          await this.updateContent(contentId, { 
            progress_percentage: 35,
            progress_status: 'thumbnail_extracting'
          });
          
          const thumbnailResult = await this.extractThumbnail(content.url, content.channel || 'Web');
          
          if (thumbnailResult?.data?.thumbnail_url) {
            await this.updateContent(contentId, { 
              thumbnail_url: thumbnailResult.data.thumbnail_url,
              progress_percentage: 60,
              progress_status: 'thumbnail_completed'
            });
            console.log('✅ Thumbnail extracted:', thumbnailResult.data.thumbnail_url);
          } else {
            console.warn('⚠️ No thumbnail extracted, using fallback');
            const { getPlatformPlaceholder } = await import('../utils/thumbnailFallbacks');
            const fallbackThumbnail = getPlatformPlaceholder(content?.channel || 'Unknown');
            await this.updateContent(contentId, { 
              thumbnail_url: fallbackThumbnail,
              progress_percentage: 60,
              progress_status: 'thumbnail_completed'
            });
          }
        } catch (error) {
          console.error('❌ Thumbnail extraction failed:', error);
          const { getPlatformPlaceholder } = await import('../utils/thumbnailFallbacks');
          const fallbackThumbnail = getPlatformPlaceholder(content?.channel || 'Unknown');
          await this.updateContent(contentId, { 
            thumbnail_url: fallbackThumbnail,
            progress_percentage: 60,
            progress_status: 'thumbnail_completed'
          });
        }
      }

      // Step 3: AI Analysis (60-100%)
      console.log('🤖 Starting AI analysis for content:', contentId);
      
      // Get transcription from content or from the variable we just set
      let transcriptionObj = null;
      let contentText = content.url || content.description || content.title || '';
      
      // If we have transcription from the variable (just processed)
      if (transcription && typeof transcription === 'string') {
        transcriptionObj = {
          full_transcript: transcription,
          text: transcription,
          transcript: transcription
        };
        contentText = transcription; // Use transcription as the primary content
        console.log('📄 Using newly transcribed text for analysis, length:', transcription.length);
      } 
      // If we have transcription from the content object (already in DB)
      else if (content.transcription) {
        if (typeof content.transcription === 'string') {
          try {
            transcriptionObj = JSON.parse(content.transcription);
          } catch (e) {
            // Not JSON, treat as plain text
            transcriptionObj = {
              full_transcript: content.transcription,
              text: content.transcription,
              transcript: content.transcription
            };
          }
        } else if (typeof content.transcription === 'object') {
          transcriptionObj = content.transcription;
        }
        contentText = transcriptionObj?.full_transcript || 
                     transcriptionObj?.text || 
                     transcriptionObj?.transcript || 
                     contentText;
        console.log('📄 Using existing transcription from database');
      }
      
      // Use URL/description if no transcription available
      if (!transcriptionObj) {
        console.log('⚠️ No transcription available, using URL/description for analysis');
      }
      
      // Ensure we have some content to analyze
      if (!contentText || contentText.trim().length === 0) {
        console.error('❌ No content available for analysis - skipping AI');
        await this.updateContent(contentId, {
          analysis_status: 'failed',
          progress_status: 'failed',
          progress_percentage: 0
        });
        return { data: false, error: { message: 'No content available for analysis' } };
      }
      
      await this.updateContent(contentId, { 
        progress_percentage: 65,
        progress_status: 'ai_analyzing'
      });

      try {
        console.log('📝 Calling OpenAI for analysis...');
        console.log('📄 Content text length:', contentText?.length || 0);
        console.log('📄 Content text preview:', contentText?.substring(0, 200));
        console.log('📄 Transcription object:', transcriptionObj ? 'present' : 'null');
        console.log('📄 Transcription has full_transcript:', transcriptionObj?.full_transcript ? 'yes' : 'no');
        
        const analysisResult = await openaiService.analyzeContentForChannelAndTags(
          contentText,
          transcriptionObj  // Pass the object, not the string
        );
        
        console.log('📊 OpenAI analysis result:', JSON.stringify(analysisResult, null, 2));

        if (analysisResult?.data) {
          aiAnalysis = {
            summary: analysisResult.data.summary,
            key_points: analysisResult.data.key_points || [],
            tags: analysisResult.data.tags || [],
            suggested_channel: analysisResult.data.channel,
            suggested_collections: analysisResult.data.suggestedCollections || [],
            confidence_score: analysisResult.data.confidence_score || 0.5,
            main_topics: analysisResult.data.main_topics || [],
            content_metadata: analysisResult.data.content_metadata || {}
          };

          // Generate better title from AI analysis
          const betterTitle = this.generateTitleFromAnalysis(aiAnalysis, content.title);
          
          // Final update: Complete (100%)
          await this.updateContent(contentId, {
            analysis_data: aiAnalysis, // ✅ Store in analysis_data (main schema)
            title: betterTitle,
            analysis_status: 'completed',
            progress_percentage: 100,
            progress_status: 'completed'
          });
          console.log('✅ AI analysis completed');
        } else {
          // NO FALLBACK - mark as failed
          console.error('❌ AI analysis failed - no data returned');
          await this.updateContent(contentId, {
            analysis_status: 'failed',
            progress_status: 'failed',
            progress_percentage: 0
          });
          return { data: false, error: { message: 'AI analysis failed' } };
        }
      } catch (error) {
        console.error('❌ AI analysis error:', error);
        console.error('❌ Error name:', error.name);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error stack:', error.stack);
        
        // NO FALLBACK - mark as failed
        await this.updateContent(contentId, {
          analysis_status: 'failed',
          progress_status: 'failed',
          progress_percentage: 0
        });
        return { data: false, error };
      }

      */

    } catch (error) {
      console.error('❌ Reprocessing failed:', error);
      await this.updateContent(contentId, { 
        analysis_status: 'failed',
        progress_status: 'failed',
        progress_percentage: 0
      });
      return { data: false, error };
    }
  }

  // Bulk reprocess all content for a user
  async bulkReprocessContent(userId) {
    try {
      console.log('🔄 Starting bulk reprocessing for user:', userId);
      
      // Get all content for the user
      const { data: allContent, error } = await this.getUserContent(userId);
      if (error || !allContent) {
        throw new Error('Failed to load content');
      }

      console.log(`📋 Found ${allContent.length} items to reprocess`);

      const results = [];
      for (const content of allContent) {
        console.log(`🔄 Reprocessing: ${content.title}`);
        const result = await this.reprocessContent(content.id);
        results.push({ id: content.id, title: content.title, success: !result.error });
        
        // Add delay between requests to avoid rate limiting and API timeouts
        // Wait 10 seconds between items to ensure each has time to complete
        await new Promise(resolve => setTimeout(resolve, 10000));
      }

      console.log('✅ Bulk reprocessing completed');
      return { data: results, error: null };
    } catch (error) {
      console.error('❌ Bulk reprocessing failed:', error);
      return { data: null, error };
    }
  }

  // Get recently viewed items (last 20) from both deepsearch.items and instagram_content
  async getRecentlyViewed(userId) {
    try {
      const allItems = [];

      // Query recently viewed from deepsearch.items
      const { data: deepsearchData, error: deepsearchError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*')
        .eq('user_id', userId)
        .not('last_viewed_at', 'is', null)
        .order('last_viewed_at', { ascending: false })
        .limit(20);

      if (deepsearchError) {
        // If column doesn't exist, silently continue
        if (!deepsearchError.message?.includes('column') && deepsearchError.code !== 'PGRST116') {
          console.warn('[ContentService] Error fetching deepsearch recently viewed:', deepsearchError);
        }
      } else if (deepsearchData) {
        // Mark source for deepsearch items
        allItems.push(...deepsearchData.map(item => ({
          ...item,
          _source: 'deepsearch.items',
          content_type: item.source_type,
        })));
      }

      // Query recently viewed from instagram_content
      const { data: instagramData, error: instagramError } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('*')
        .eq('user_id', userId)
        .not('last_viewed_at', 'is', null)
        .order('last_viewed_at', { ascending: false })
        .limit(20);

      if (instagramError) {
        // If column doesn't exist, silently continue
        if (!instagramError.message?.includes('column') && instagramError.code !== 'PGRST116') {
          console.warn('[ContentService] Error fetching instagram recently viewed:', instagramError);
        }
      } else if (instagramData) {
        // Mark source for instagram items
        allItems.push(...instagramData.map(item => ({
          ...item,
          _source: 'instagram_content',
        })));
      }

      // Sort combined results by last_viewed_at descending
      allItems.sort((a, b) => new Date(b.last_viewed_at) - new Date(a.last_viewed_at));

      // Limit to 20 total
      const limitedItems = allItems.slice(0, 20);

      return { data: limitedItems, error: null };
    } catch (error) {
      console.error('[ContentService] Error in getRecentlyViewed:', error);
      return { data: [], error: null };
    }
  }

  // Track item view in both deepsearch.items and instagram_content (silently fail, no UI changes)
  async trackItemView(itemId, userId) {
    const timestamp = new Date().toISOString();
    
    try {
      // Try to update in deepsearch.items first
      const { error: deepsearchError, data: deepsearchData } = await supabase
        .schema('deepsearch')
        .from('items')
        .update({ last_viewed_at: timestamp })
        .eq('id', itemId)
        .eq('user_id', userId)
        .select('id');

      // If updated in deepsearch.items, we're done
      if (!deepsearchError && deepsearchData && deepsearchData.length > 0) {
        return;
      }

      // Otherwise try instagram_content
      const { error: instagramError } = await supabase
        .schema('public')
        .from('instagram_content')
        .update({ last_viewed_at: timestamp })
        .eq('id', itemId)
        .eq('user_id', userId);

      // Silently fail - view tracking is non-critical
      if (instagramError) {
        return;
      }
    } catch (error) {
      // Silently fail - view tracking is non-critical
      return;
    }
  }
}

export default new ContentService();
